import { useMatch } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { ExpandableSearch } from '@components/expandable-search';
import { RouterTabs } from '@components/header/router-tabs';
import { SearchBox } from '@components/search-box';
import { Stack } from '@components/stack';
import { useIsMobile } from '@hooks/use-is-mobile';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { FilterButton } from 'components/FilterButton';
import { Header } from 'components/headers';
import { JobBulkActions } from 'components/job-bulk-actions';
import { JobOptions } from 'components/JobOptions';
import { FavoriteToggle } from 'components/navigation/favorites';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useJobTabs } from 'hooks/useJobTabs';
import { useStore } from 'models/Provider';

export const JobHeader = observer(({ job, filters }) => {
  const { hasRolePermission } = usePermissions();
  if (!job) return <Header />;
  const section = useMatch('/jobs/:jobID/:section/*')?.params.section;
  const showBulk =
    (job.listView.selectedApplications || []).length > 0 &&
    hasRolePermission('applicants:use-bulk-actions');
  return (
    <Header
      selectionHeader={<JobBulkActions job={job} />}
      showSelectionHeader={showBulk}
      mobileTitle={job.detailLoaded ? job.title : ''}
      tabs={<JobTabs job={job} section={section} />}
      breadcrumbs={<JobCrumbs job={job} />}
      actions={<JobActions job={job} filters={filters} section={section} />}
    />
  );
});

const JobTabs = observer(({ job, section }) => {
  const { t } = useStore();
  const tabs = useJobTabs(job);

  return (
    <RouterTabs
      ariaLabel={t('job.tabs.label')}
      activeSection={section}
      tabs={tabs.map((tab) => ({
        label: tab.display,
        to: tab.link,
        section: tab.value,
        icon: tab.icon,
        dataCy: `job-tabs:${tab.value}`,
      }))}
    />
  );
});

const JobCrumbs = observer(({ job }) => {
  const { t } = useStore();

  let items = [{ label: t('navigation.jobs'), link: '/jobs' }];
  if (job.detailLoaded) {
    items.push({ label: job.title });
  }

  return (
    <Stack orientation="horizontal" space={2} align="center">
      <Breadcrumb items={items} />
      <FavoriteToggle contentType="job" objectId={job.id} />
    </Stack>
  );
});

const JobActions = observer(({ job, filters, section }) => {
  return (
    <>
      <Stack orientation="horizontal" space={2} justify="flex-end" style={{ flexGrow: 1 }}>
        {section === 'list' && <JobListActions job={job} filters={filters} />}
        {section === 'info' && <JobInfoActions job={job} />}
        {section === 'pipeline' && <JobPipelineActions job={job} filters={filters} />}
        {section === 'statistics' && <div id="job-statistics-actions" />}
        {section === 'promote' && <div id="job-promote-actions" />}
      </Stack>
    </>
  );
});

const AddApplicant = observer(({ job, fullWidth = false }) => {
  const { t } = useStore();
  const { hasRolePermission } = usePermissions();
  const { showModal } = useModal();
  if (!hasRolePermission('application:add')) {
    return null;
  }
  return (
    <Button
      style={{ ...(fullWidth && { width: '100%' }) }}
      onClick={() => {
        showModal('ADD_APPLICANT', {
          _container: {
            closeOnOverlayClickOrEsc: false,
          },
          job,
        });
      }}
      text={t('job.addapplicant')}
    />
  );
});

// Mobile uses SearchBox (always-visible); desktop uses ExpandableSearch (collapses to icon).
const JobApplicationSearch = observer(({ job }) => {
  const { t } = useStore();
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <SearchBox
        placeholder={t('job.applications.search.tip')}
        value={job.settings.searchStr}
        onChange={job.settings.setSearchValue}
        isFullWidth
      />
    );
  }
  return (
    <ExpandableSearch
      value={job.settings.searchStr}
      onChange={job.settings.setSearchValue}
      onChangeDebounce={0}
      placeholder={t('job.applications.search.tip')}
    />
  );
});

const JobMobileActions = observer(({ job, filters }) => (
  <JobMobileActionsContainer>
    <Stack space={2}>
      <AddApplicant job={job} />
      <Stack space={2} orientation="horizontal">
        <SearchSlot>
          <JobApplicationSearch job={job} />
        </SearchSlot>
        <FilterButton filters={filters} />
      </Stack>
    </Stack>
  </JobMobileActionsContainer>
));

const JobDesktopActions = observer(({ job, filters }) => {
  const { hasRolePermission } = usePermissions();
  return (
    <>
      <JobApplicationSearch job={job} />
      <FilterButton filters={filters} />
      {hasRolePermission('job:options') && <JobOptions job={job} transparent={false} />}
      <AddApplicant job={job} />
    </>
  );
});

const JobListActions = observer(({ job, filters }) => {
  const isMobile = useIsMobile();
  return isMobile ? (
    <JobMobileActions job={job} filters={filters} />
  ) : (
    <JobDesktopActions job={job} filters={filters} />
  );
});

const JobPipelineActions = observer(({ job, filters }) => {
  const isMobile = useIsMobile();
  return isMobile ? (
    <JobMobileActions job={job} filters={filters} />
  ) : (
    <JobDesktopActions job={job} filters={filters} />
  );
});

const JobMobileActionsContainer = styled.div((p) => ({
  padding: pxToRem(24),
  width: '100%',
  backgroundColor: theme.color.backgroundLightDark,
  '> *': {
    width: '100%',
  },
}));

const SearchSlot = styled.div({
  flexGrow: 1,
});

const JobInfoActions = observer(({ job }) => {
  const { hasRolePermission } = usePermissions();
  return <>{hasRolePermission('job:options') && <JobOptions job={job} transparent={false} />}</>;
});
